import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/lib/i18n";
import { LanguageSelector } from "@/components/shared/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/shared/Logo";
import { ArrowLeft, Target, Eye, Heart } from "lucide-react";

export default function AboutPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <header className="bg-navy-900 border-b border-navy-800">
        <div className="max-w-4xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <div onClick={() => navigate("/")}><Logo size="md" className="text-white cursor-pointer" /></div>
          <LanguageSelector variant="dark" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <Button variant="ghost" className="mb-6 text-slate-600 font-body" onClick={() => navigate(-1)} data-testid="about-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> {t("contact.back")}
        </Button>

        <article className="space-y-10">
          {/* Header */}
          <div>
            <h1 className="font-sans font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight mb-3" data-testid="about-title">
              {t("about.title")}
            </h1>
            <p className="text-lg text-emerald-600 font-semibold font-body">{t("about.tagline")}</p>
          </div>

          {/* Intro */}
          <div className="space-y-4 text-base text-slate-600 font-body leading-relaxed">
            <p>{t("about.intro1")}</p>
            <p>{t("about.intro2")}</p>
            <p className="font-semibold text-slate-800">{t("about.intro3")}</p>
            <p>{t("about.intro4")}</p>
          </div>

          {/* CEO Photo */}
          <div className="flex justify-center">
            <figure className="text-center" data-testid="about-ceo-photo">
              <img
                src="/ceo-malene-dale.jpg"
                alt="Malene Dale, CEO de Staffera"
                className="w-44 h-44 rounded-full object-cover object-top mx-auto shadow-lg border-4 border-white ring-2 ring-slate-200"
              />
              <figcaption className="mt-3 text-xs text-slate-400 font-body">Malene Dale, CEO de Staffera</figcaption>
            </figure>
          </div>

          {/* Mission */}
          <div className="bg-slate-50 rounded-xl p-6 md:p-8 border border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Target className="w-5 h-5 text-emerald-600" />
              </div>
              <h2 className="font-sans font-bold text-xl text-slate-900">{t("about.mission_title")}</h2>
            </div>
            <p className="font-semibold text-slate-800 font-body mb-2">{t("about.mission_subtitle")}</p>
            <p className="text-base text-slate-600 font-body leading-relaxed">{t("about.mission_desc")}</p>
          </div>

          {/* Vision */}
          <div className="bg-slate-50 rounded-xl p-6 md:p-8 border border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-sky-500/10 flex items-center justify-center">
                <Eye className="w-5 h-5 text-sky-600" />
              </div>
              <h2 className="font-sans font-bold text-xl text-slate-900">{t("about.vision_title")}</h2>
            </div>
            <p className="font-semibold text-slate-800 font-body mb-2">{t("about.vision_subtitle")}</p>
            <p className="text-base text-slate-600 font-body leading-relaxed">{t("about.vision_desc")}</p>
          </div>

          {/* Closing */}
          <div className="text-center py-6 space-y-3">
            <p className="text-base text-slate-600 font-body leading-relaxed">{t("about.closing1")}</p>
            <p className="font-sans font-bold text-xl text-slate-900">{t("about.closing2")}</p>
            <div className="flex items-center justify-center gap-2 pt-2">
              <Heart className="w-4 h-4 text-emerald-500" />
              <p className="text-sm text-slate-500 font-body italic">{t("about.slogan")}</p>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
